<script lang="ts" setup>
// components
import { Dropdown as VDropdown } from "floating-vue";
import { GButton } from "@gwrk/g-components";
import AccountControls from "@/components/molecules/AccountControls.vue";

// composables
import { useI18n } from "vue-i18n";
import { useUser } from "@/stores/useUser";
import { useCompanyId } from "@/stores/useCompanyId";
import { useToast } from "vue-toastification";

// enums
import {
  ButtonVariant,
  Size,
  IconPlacement,
  Icon,
} from "@gwrk/g-components/enums";
import { Route } from "@/enums/Route";
import { LegacyClientPanelUrl } from "@/enums/LegacyClientPanelUrl";

const emit = defineEmits<{
  (event: "toggle"): void;
}>();

const { t } = useI18n();
const userStore = useUser();
const companyIdStore = useCompanyId();
const toast = useToast();

const logoSrc = `/${
  import.meta.env.VITE_APP_BASE_URL
}/images/svg/gowork-logo.svg`;

const logout = async () => {
  try {
    await userStore.logout();

    window.location.href = LegacyClientPanelUrl.PANEL;
  } catch (error) {
    toast.error(t("error.send_data_error"));
    console.error(error);
  }
};
</script>

<template>
  <nav class="g-navbar">
    <GButton
      class="mr-auto !hidden lg:!inline-flex"
      :href="{
        name: Route.JOB_OFFERS_LISTING_ACTIVE,
        params: { companyId: companyIdStore.companyId },
      }"
      :button-variant="ButtonVariant.LINK"
      :padding="false"
    >
      <img class="g-navbar__logo" :alt="t('gowork.pl')" :src="logoSrc" />
    </GButton>
    <VDropdown>
      <GButton
        :button-variant="ButtonVariant.BLUE"
        :icon="Icon.PLUS_IN_CIRCLE_SOLID"
        data-cy="add-offer-button"
      >
        {{ t("navigation.add_offer") }}
      </GButton>
      <template #popper>
        <div class="flex flex-col gap-2 items-center">
          <GButton
            :button-variant="ButtonVariant.TRANSPARENT"
            :href="{
              name: Route.JOB_OFFER_ADD_STANDARD_PLUS,
              params: { companyId: companyIdStore.companyId },
            }"
          >
            {{ t("job_offer_form.type.standard_plus") }}
          </GButton>
          <GButton
            :button-variant="ButtonVariant.TRANSPARENT"
            :href="{
              name: Route.JOB_OFFER_ADD_PREMIUM,
              params: { companyId: companyIdStore.companyId },
            }"
          >
            {{ t("job_offer_form.type.premium") }}
          </GButton>
          <GButton
            :button-variant="ButtonVariant.TRANSPARENT"
            :href="{
              name: Route.JOB_OFFER_ADD_PRO,
              params: { companyId: companyIdStore.companyId },
            }"
          >
            {{ t("job_offer_form.type.pro") }}
          </GButton>
        </div>
      </template>
    </VDropdown>
    <AccountControls />
    <VDropdown class="hidden lg:block">
      <GButton
        :button-variant="ButtonVariant.TRANSPARENT"
        :icon-placement="IconPlacement.AFTER"
        :icon="Icon.CHEVRON_DOWN_SOLID"
        :icon-size="Size.SM"
      >
        {{ t("navigation.my_account") }}
      </GButton>
      <template #popper>
        <div class="flex flex-col items-center gap-2">
          <GButton :button-variant="ButtonVariant.TRANSPARENT" @click="logout">
            {{ t("btn.logout") }}
          </GButton>
        </div>
      </template>
    </VDropdown>

    <GButton
      class="md:!hidden"
      :padding="false"
      :button-variant="ButtonVariant.TRANSPARENT"
      :icon="Icon.HAMBURGER_SOLID"
      @click="emit('toggle')"
    />
  </nav>
</template>

<style lang="scss" scoped>
.g-navbar {
  position: sticky;
  top: 0;
  z-index: 10;
  border-bottom: 1px solid theme("colors.gray.300");
  height: 5rem;

  @apply flex items-center bg-white p-4 gap-4;

  &__logo {
    height: 3rem;
  }
}
</style>
